import type { Role } from '@travel-planner/shared/schemas/auth';
import type { RoleGroupId } from '../model/role-catalog';
import { joinCss } from '@/shared/lib/css-utils';
import { Badge, Text } from '@/shared/ui';
import { ROLE_GROUPS, roleOptionsByGroup } from '../model/role-catalog';
import css from './role-picker.module.scss';

type RolePickerProps = {
  // All roles selectable by the current actor (already filtered for the
  // admin-grant guard upstream).
  available: readonly Role[];
  value: Role[];
  onChange: (roles: Role[]) => void;
  // Restrict which governance groups render (default: all). Lets a caller split
  // the picker into side-by-side columns — one instance per group — while
  // selection stays a single shared `value`/`onChange` pair.
  groups?: readonly RoleGroupId[];
};

const CLEARANCE_VARIANT = {
  L1: 'low',
  L2: 'medium',
  L3: 'high',
} as const;

// Grouped, self-documenting role selector. Platform roles and Eleggia DMC roles
// are shown in labelled sections; each Eleggia role carries its capability
// description and the field-level clearance (L1/L2/L3) it confers, so an HQ Admin
// can grant roles without cross-referencing the Tech Spec.
export const RolePicker = ({
  available,
  value,
  onChange,
  groups,
}: RolePickerProps) => {
  const toggle = (role: Role) => {
    onChange(
      value.includes(role) ? value.filter((r) => r !== role) : [...value, role]
    );
  };

  const visibleGroups = groups
    ? ROLE_GROUPS.filter((g) => groups.includes(g.id))
    : ROLE_GROUPS;

  return (
    <div className={css.root}>
      {visibleGroups.map((group) => {
        const options = roleOptionsByGroup(available, group.id);
        if (options.length === 0) return null;
        return (
          <fieldset className={css.group} key={group.id}>
            <legend className={css.groupLegend}>{group.label}</legend>
            <Text as="p" className={css.groupHint} size="sm">
              {group.hint}
            </Text>
            <div className={css.options}>
              {options.map((opt) => {
                const checked = value.includes(opt.value);
                return (
                  <label
                    className={checked ? css.optionIsChecked : css.option}
                    key={opt.value}
                  >
                    <input
                      checked={checked}
                      className={joinCss(
                        css.checkbox,
                        opt.clearance ? css.checkboxWithBadge : ''
                      )}
                      onChange={() => toggle(opt.value)}
                      type="checkbox"
                    />
                    <span className={css.optionBody}>
                      <span className={css.optionHeader}>
                        <span className={css.optionLabel}>{opt.label}</span>
                        {opt.clearance && (
                          <Badge variant={CLEARANCE_VARIANT[opt.clearance]}>
                            {opt.clearance}
                          </Badge>
                        )}
                      </span>
                      {opt.description && (
                        <span className={css.optionDescription}>
                          {opt.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
};
